import React from "react";

interface MetricCardProps {
  title: string;
  value: string;
  change?: string;
  icon?: React.ReactNode;
  trend?: "up" | "down";
  onClick?: () => void;
  clickable?: boolean;
  /**
   * Highlight the card with the theme's accent pair (the same green used for
   * "up" metrics on the dashboard) instead of a plain panel. For stat rows that
   * should stand out, e.g. the money totals on Reports.
   */
  accent?: boolean;
  className?: string;
  titleClassName?: string;
  valueClassName?: string;
}

/**
 * Colours come from the `--dash-*` custom properties published by the store's
 * theme (theme/AppTheme.tsx), so a card follows the active preset. Callers
 * should use `accent` rather than passing fixed colour classes — a hard-coded
 * emerald override survives the theme and clashes with every non-green preset.
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon,
  trend = "up",
  onClick,
  clickable = false,
  accent = false,
  className = "",
  titleClassName = "",
  valueClassName = "",
}) => {
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
      // Dispatch custom event for Live Display shake effect
      window.dispatchEvent(new CustomEvent("shakeTickets"));
    }
  };

  const up = trend === "up";

  const accentStyle: React.CSSProperties = accent
    ? {
        background: "var(--dash-icon-bg)",
        borderColor: "var(--dash-panel-border)",
      }
    : {};

  return (
    <div
      style={accentStyle}
      className={`${
        accent ? "" : "app-surface"
      } backdrop-blur-xl rounded-2xl shadow-xl border app-border p-6 transition-all duration-300 ${
        clickable
          ? "cursor-pointer hover:shadow-2xl hover:scale-105 hover:-translate-y-1 active:scale-95"
          : "hover:shadow-lg hover:-translate-y-0.5"
      } ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p
            className={`text-sm font-medium ${titleClassName}`}
            style={{
              color: accent ? "var(--dash-icon-text)" : "var(--dash-muted)",
            }}
          >
            {title}
          </p>
          <p
            className={`text-2xl font-bold ${valueClassName}`}
            style={{
              color: accent ? "var(--dash-icon-text)" : "var(--dash-value)",
              fontFamily: "var(--dash-numeric-font)",
            }}
          >
            {value}
          </p>
        </div>
        {!!icon && (
          <div
            className="rounded-xl p-3 shadow-lg"
            style={{
              background: up
                ? "var(--dash-icon-bg)"
                : "var(--dash-icon-bg-down)",
              color: up ? "var(--dash-icon-text)" : "var(--dash-icon-text-down)",
              border: `var(--dash-border-width) solid ${
                up ? "var(--dash-icon-border)" : "var(--dash-icon-border-down)"
              }`,
            }}
          >
            {icon}
          </div>
        )}
      </div>
      {!!change && (
        <div className="mt-2 flex items-center">
          <span
            className="text-sm font-medium"
            style={{
              color: up
                ? "var(--dash-icon-text)"
                : "var(--dash-icon-text-down)",
            }}
          >
            {change}
          </span>
          <span className="text-sm app-muted ml-1">from last period</span>
        </div>
      )}
    </div>
  );
};
